import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useInventoryProcesses,
  useCreateInventory,
  useStartInventory,
  useCloseInventory,
  useDeleteInventory,
  InventoryProcess,
} from '@/api/hooks/useInventory';
import { useLocationsDropdown } from '@/api/hooks/useLocations';
import { useClientsDropdown } from '@/api/hooks/useClients';
import {
  Plus,
  ClipboardList,
  CheckCircle,
  Clock,
  Play,
  Eye,
  Trash2,
  Filter,
  X,
  PlayCircle,
  StopCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { isSGSTenant } from '@/config/tenant';
import { toast } from 'sonner';

type InventoryStatus = 'Open' | 'InProgress' | 'Closed';

export default function InventoryPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();

  // Status label helper
  const getStatusLabel = (status: InventoryStatus) => {
    switch (status) {
      case 'Open': return t('status.open');
      case 'InProgress': return t('status.inProgress');
      case 'Closed': return t('status.closed');
      default: return status;
    }
  };

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Dialog state
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newInventoryLocation, setNewInventoryLocation] = useState<string>('');
  const [deleteInventory, setDeleteInventory] = useState<InventoryProcess | null>(null);
  const [confirmClose, setConfirmClose] = useState<InventoryProcess | null>(null);

  // Fetch data
  const {
    data: inventoryResponse,
    isLoading,
    error,
  } = useInventoryProcesses({
    status: statusFilter || undefined,
    locationId: locationFilter ? parseInt(locationFilter) : undefined,
    clientId: clientFilter ? parseInt(clientFilter) : undefined,
    page,
    pageSize,
  });

  const { data: locations } = useLocationsDropdown();
  const { data: clients } = useClientsDropdown();

  // Mutations
  const createMutation = useCreateInventory();
  const startMutation = useStartInventory();
  const closeMutation = useCloseInventory();
  const deleteMutation = useDeleteInventory();

  // Calculate stats
  const stats = useMemo(() => {
    const items = inventoryResponse?.items ?? [];
    return {
      open: items.filter((i) => i.status === 'Open').length,
      inProgress: items.filter((i) => i.status === 'InProgress').length,
      closed: items.filter((i) => i.status === 'Closed').length,
    };
  }, [inventoryResponse]);

  // Handlers
  const handleCreateInventory = async () => {
    if (!newInventoryLocation) return;
    try {
      await createMutation.mutateAsync({
        locationId: parseInt(newInventoryLocation),
      });
      setShowNewDialog(false);
      setNewInventoryLocation('');
    } catch (error) {
      console.error('Failed to create inventory:', error);
    }
  };

  const handleStartInventory = async (id: number) => {
    try {
      await startMutation.mutateAsync({ locationId: id });
    } catch (error) {
      console.error('Failed to start inventory:', error);
    }
  };

  const handleCloseInventory = async () => {
    if (!confirmClose) return;
    try {
      await closeMutation.mutateAsync(confirmClose.id);
      setConfirmClose(null);
    } catch (error) {
      console.error('Failed to close inventory:', error);
    }
  };

  const handleDeleteInventory = async () => {
    if (!deleteInventory) return;
    try {
      await deleteMutation.mutateAsync(deleteInventory.id);
      setDeleteInventory(null);
      toast.success(t('toast.deleted'));
    } catch (error) {
      // El servidor rechaza eliminar un inventario cerrado o con lecturas, y
      // ese motivo tiene que llegar al operador: tragárselo en consola es
      // indistinguible de que el botón no haga nada.
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || t('toast.deleteFailed'));
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setLocationFilter('');
    setClientFilter('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter || locationFilter || clientFilter;

  const getStatusIcon = (status: InventoryStatus) => {
    switch (status) {
      case 'Open':
        return <Clock className="h-4 w-4" />;
      case 'InProgress':
        return <Play className="h-4 w-4" />;
      case 'Closed':
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: InventoryStatus) => {
    switch (status) {
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      case 'InProgress':
        return 'bg-blue-100 text-blue-700';
      case 'Closed':
        return 'bg-green-100 text-green-700';
    }
  };

  const inventories = inventoryResponse?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pageTitle')}</h1>
          <p className="text-muted-foreground">
            {t('pageDescription')}
          </p>
        </div>
        {!isSGSTenant() && (
          <Button onClick={() => setShowNewDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('newProcess')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.openProcesses')}</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.open}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.inProgress')}</CardTitle>
            <Play className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.inProgress}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.closedToday')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.closed}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('cardTitle')}</CardTitle>
          <CardDescription>{t('cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-end gap-4">
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {showFilters && (
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.status')}</label>
                  <Select
                    value={statusFilter || 'all'}
                    onValueChange={(v) => {
                      setStatusFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('status.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('status.all')}</SelectItem>
                      <SelectItem value="Open">{t('status.open')}</SelectItem>
                      <SelectItem value="InProgress">{t('status.inProgress')}</SelectItem>
                      <SelectItem value="Closed">{t('status.closed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.location')}</label>
                  <Select
                    value={locationFilter || 'all'}
                    onValueChange={(v) => {
                      setLocationFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('filters.allLocations')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.allLocations')}</SelectItem>
                      {locations?.map((loc) => (
                        <SelectItem key={loc.id} value={String(loc.id)}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.client')}</label>
                  <Select
                    value={clientFilter || 'all'}
                    onValueChange={(v) => {
                      setClientFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('filters.allClients')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.allClients')}</SelectItem>
                      {clients?.map((client) => (
                        <SelectItem key={client.id} value={String(client.id)}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    {t('filters.clearFilters')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Inventory List */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {inventories.map((inventory) => (
                <div
                  key={inventory.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center gap-4">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <ClipboardList className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{inventory.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {inventory.locationName || t('allLocations')}
                        {inventory.clientName && (
                          <> · <span className="text-primary font-medium">{inventory.clientName}</span></>
                        )}
                        {' '}| {t('started')}:{' '}
                        {format(new Date(inventory.startDate), "d 'de' MMM yyyy", { locale: es })}
                        {inventory.endDate && (
                          <> | {t('ended')}: {format(new Date(inventory.endDate), "d 'de' MMM yyyy", { locale: es })}</>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {inventory.scannedAssets} / {inventory.totalAssets}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {inventory.missingAssets > 0 && (
                          <span className="text-red-600">
                            {inventory.missingAssets} {t('missing')}
                          </span>
                        )}
                      </p>
                    </div>

                    {inventory.status !== 'Closed' && inventory.totalAssets > 0 && (
                      <div className="w-24">
                        <div className="h-2 rounded-full bg-muted">
                          <div
                            className="h-2 rounded-full bg-primary transition-all"
                            style={{
                              width: `${(inventory.scannedAssets / inventory.totalAssets) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                        inventory.status
                      )}`}
                    >
                      {getStatusIcon(inventory.status)}
                      {getStatusLabel(inventory.status)}
                    </span>

                    <div className="flex gap-2">
                      {inventory.status === 'Open' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartInventory(inventory.id)}
                          disabled={startMutation.isPending}
                        >
                          <PlayCircle className="mr-2 h-3 w-3" />
                          {t('actions.start')}
                        </Button>
                      )}
                      {inventory.status === 'InProgress' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmClose(inventory)}
                        >
                          <StopCircle className="mr-2 h-3 w-3" />
                          {t('actions.close')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/inventory/${inventory.id}`)}
                      >
                        <Eye className="mr-2 h-3 w-3" />
                        {t('actions.view')}
                      </Button>
                      {inventory.status === 'Open' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteInventory(inventory)}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {inventories.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {t('noProcesses')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Inventory Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('form.createTitle')}</DialogTitle>
            <DialogDescription>
              {t('form.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('form.locationLabel')}</label>
              <Select value={newInventoryLocation} onValueChange={setNewInventoryLocation}>
                <SelectTrigger>
                  <SelectValue placeholder={t('form.locationPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {locations?.map((loc) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleCreateInventory}
              disabled={!newInventoryLocation || createMutation.isPending}
            >
              {createMutation.isPending ? t('common:status.processing') : t('actions.createInventory')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmClose}
        onOpenChange={(open) => !open && setConfirmClose(null)}
        title={t('dialog.closeTitle')}
        description={t('dialog.closeConfirm', { name: confirmClose?.name })}
        confirmLabel={t('dialog.closeInventory')}
        cancelLabel={t('common:buttons.cancel')}
        onConfirm={handleCloseInventory}
        isLoading={closeMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteInventory}
        onOpenChange={(open) => !open && setDeleteInventory(null)}
        title={t('dialog.deleteTitle')}
        description={t('dialog.deleteConfirm', { name: deleteInventory?.name })}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDeleteInventory}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
